/**
 * 製品をまたぐ会社の切替の受け口（#151、docs/60 §2「移るとき」・★d・★e・§5 POST /auth/handoff）。/handoff は Shell の外・ログインの有無を問わない。
 *   ・鍵は URL の # の後ろ（t=…。サーバーのログ・Referer に残さない）。1回だけ読んで、すぐ history.replaceState で # を消してから POST /auth/handoff
 *   ・200 → このドメインで入った（同じ人のログインがあった・「切替」を押した）→ /home（トースト「{会社名} に切り替えました」）
 *   ・428 HANDOFF_CONFIRM（このドメインにログインが無い）→ 確認の画面 → 「切替」で confirm:true を送り直す
 *     （他人が作った鍵のリンクを踏まされて、知らないうちに他人の会社に入るのを防ぐ）
 *   ・409 HANDOFF_OTHER_ACCOUNT（別の人がログイン中）→ 切らずに止める。朱の注意の帯＋「ログアウトして切替」で POST /auth/logout → confirm:true
 *   ・それ以外（使用済み・期限切れ・別の端末・鍵が無い）→「切り替えられませんでした。会社の切替からもう一度お試しください」＋
 *     このドメインでログインしていれば「会社切替へ」（/choose?switch）、していなければ「ログイン」（#192、docs/76 §0.3 ト。
 *     ログインしているかが分かるまでは回る印のまま＝ボタンの行き先があとから変わらない）。
 *     鍵の無い /handoff はログインの next にしない（contract の loginPathWithNext）＝ログインのあとにここへ戻って止まらない
 * 確認の画面（#194、2026-10-16 射場さん：メールアドレスでなく氏名で。docs/60 §10、Figma「会社の切替の確認（10/16）」182:157 ①〜④。招待の確認＝#185 と同じ型）：
 *   題「会社の切替」→（④ 別の人がログイン中なら朱の注意の帯）→（PC だけ一文「次の会社に切り替えます。」）→ カード
 *   （会社のアイコン 56px・会社名・製品の印と製品名 → 区切り → 「利用者　姓 名」→ 区切り → 「ログインID　メール」（14px・薄墨））→ ボタン → 注。
 *   ③ 氏名の無い人（姓・名とも空）は「利用者」の欄にメールを出し、「ログインID」の行は出さない。
 *   スマホ版は白い紙1枚・カードは影・「切替」は幅いっぱい 48px。PC は真ん中に 560px の枠・カードは枠線・右寄せの「取消」「切替」
 *   （「取消」は window.close() ができないので、このドメインでログインしていれば /home、していなければ /login）。
 *   製品はこのドメインの製品（＝切り替える先の会社の製品。ME の製品ではない＝開発で2つの製品が同じ番地にあっても違わない）。
 *   色は lib/theme.ts で /handoff をログイン前の画面に入れた＝製品の既定のテーマ色・ライト（別の人のログインの色を当てない）
 *   見た目はログイン前の画面（.authpage・上の帯のロゴ・.authcard・スマホ版は枠の頭に AuthLogo・下の帯）
 */
import { HandoffInfo, SWITCHER_MSG, fullName, type HandoffRequest, type Me } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError } from "../api.js";
import { setMe, useMe } from "../auth.js";
import { usePageName } from "../components/BackButton.jsx";
import { CompanyIcon } from "../components/CompanyIcon.jsx";
import { AuthLogo, SiteFooter } from "../components/ui.jsx";
import { PRELOGIN_PRODUCT, productInfo } from "../lib/product.js";
import { useLogoHeader } from "../components/LightArea.jsx";

type View = { k: "loading" } | { k: "confirm"; info: HandoffInfo } | { k: "other"; info: HandoffInfo } | { k: "failed" };

/** URL の #t=… の鍵（無ければ null） */
function tokenOfHash(): string | null {
  const h = window.location.hash.replace(/^#/, "");
  return h ? new URLSearchParams(h).get("t") || null : null;
}

export function HandoffPage() {
  // このドメインの製品（ME の製品ではない。上の説明）
  const P = productInfo(PRELOGIN_PRODUCT);
  const logoHeader = useLogoHeader(P.product); // ダークでは暗い地用（#191 の2回目）
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  usePageName(SWITCHER_MSG.title, "会社切替"); // 戻るボタンは「を」「の」なし（#240）
  // 鍵は1回だけ読む（# はすぐ消す）
  const [token] = useState(tokenOfHash);
  const [view, setView] = useState<View>(token ? { k: "loading" } : { k: "failed" });
  const started = useRef(false);

  const send = async (confirm: boolean) => {
    if (!token) return setView({ k: "failed" });
    setView({ k: "loading" });
    try {
      const body: HandoffRequest = confirm ? { token, confirm: true } : { token };
      const m = await api<Me>("POST", "/auth/handoff", body);
      await setMe(qc, m);
      void qc.invalidateQueries();
      nav("/home", { replace: true, state: { switched: m.tenant.name } });
    } catch (e) {
      if (e instanceof ApiError) {
        const info = HandoffInfo.safeParse(e.problem);
        if (e.code === "HANDOFF_CONFIRM" && info.success) return setView({ k: "confirm", info: info.data });
        if (e.code === "HANDOFF_OTHER_ACCOUNT" && info.success) return setView({ k: "other", info: info.data });
      }
      setView({ k: "failed" });
    }
  };

  /** 別の人のログインを切ってから入る（元のドメインのログインはそのまま） */
  const logoutAndEnter = async () => {
    setView({ k: "loading" });
    try {
      await api<void>("POST", "/auth/logout");
    } catch {
      return setView({ k: "failed" });
    }
    await setMe(qc, null);
    await send(true);
  };

  useEffect(() => {
    // StrictMode でも1回だけ（鍵は1回きり）
    if (started.current) return;
    started.current = true;
    if (window.location.hash) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    if (token) void send(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 切り替える先のカード：会社（アイコン・会社名・製品）→ 利用者 →（氏名のある人だけ）ログインID */
  const card = (info: HandoffInfo) => {
    const person = info.person ? fullName(info.person) : "";
    return (
      <div className="hoff-card" data-testid="handoff-card">
        <div className="hoff-co" data-testid="handoff-company">
          <CompanyIcon name={info.company.name} logoUrl={info.company.logoUrl} initial={info.company.initial} size={56} />
          <span className="hoff-tx">
            <b className="hoff-name">{info.company.name || SWITCHER_MSG.noName}</b>
            <span className="hoff-prod" data-testid="handoff-product">
              <img src={P.assets.logoMark} alt="" width={18} height={18} />
              {P.name}
            </span>
          </span>
        </div>
        <dl className="hoff-rows">
          <div className="hoff-row" data-testid="handoff-person">
            <dt>{SWITCHER_MSG.handoffPerson}</dt>
            <dd>{person || info.email}</dd>
          </div>
          {person ? (
            <div className="hoff-row is-sub" data-testid="handoff-login-id">
              <dt>{SWITCHER_MSG.handoffLoginId}</dt>
              <dd>{info.email}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    );
  };

  /** 確認の画面（② ログインなし／④ 別の人がログイン中） */
  const confirmView = (info: HandoffInfo, other: boolean) => (
    <>
      {other ? (
        <p className="hoff-warn" data-testid="handoff-other">
          {SWITCHER_MSG.handoffOtherBand(info.currentEmail)}
        </p>
      ) : null}
      <p className="hoff-lead hoff-pc">{SWITCHER_MSG.handoffLead}</p>
      {card(info)}
      <div className="hoff-acts">
        <Link className="btn hoff-pc" to={me.data ? "/home" : "/login"} data-testid="handoff-cancel">
          {SWITCHER_MSG.cancel}
        </Link>
        {other ? (
          <button type="button" className="btn primary hoff-go" onClick={() => void logoutAndEnter()} data-testid="handoff-logout-enter">
            {SWITCHER_MSG.handoffLogoutEnter}
          </button>
        ) : (
          <button type="button" className="btn primary hoff-go" onClick={() => void send(true)} data-testid="handoff-enter">
            {SWITCHER_MSG.handoffGo}
          </button>
        )}
      </div>
      <p className="hoff-note" data-testid="handoff-note">
        {other ? SWITCHER_MSG.handoffOtherNote : SWITCHER_MSG.handoffNothing}
      </p>
    </>
  );

  return (
    <div className="authpage" data-product={P.product} data-testid="handoff">
      <header className="authtop">
        <a href="/" className="brand">
          <img src={logoHeader} alt={P.name} width={320} height={100} />
        </a>
      </header>
      <main className="auth hoffauth">
        <div className="authcard hoff">
          {/* スマホ版は上の帯を出さないので、枠の頭にロゴ（#115） */}
          <AuthLogo phoneOnly product={P.product} />
          <h1>{SWITCHER_MSG.title}</h1>
          {view.k === "loading" || (view.k === "failed" && me.isPending) ? (
            <p className="hoff-wait" role="status">
              <span className="gspin" aria-hidden="true" />
              {SWITCHER_MSG.moving}
            </p>
          ) : view.k === "confirm" ? (
            confirmView(view.info, false)
          ) : view.k === "other" ? (
            confirmView(view.info, true)
          ) : (
            <>
              <p className="err" role="alert" data-testid="handoff-error">
                ⚠ {SWITCHER_MSG.handoffFailed}
              </p>
              {me.data ? (
                <Link className="btn primary big" to="/choose?switch" data-testid="handoff-to-switcher">
                  {SWITCHER_MSG.handoffToSwitcher}
                </Link>
              ) : (
                <Link className="btn primary big" to="/login" data-testid="handoff-login">
                  {SWITCHER_MSG.handoffLogin}
                </Link>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter band />
    </div>
  );
}
